import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ChevronRight } from 'lucide-react';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { MarketData } from '../services/binanceService';

interface MarketOverviewProps {
  onSymbolSelect?: (symbol: string) => void;
}

const MarketOverview: React.FC<MarketOverviewProps> = ({ onSymbolSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState('Crypto');
  
  // Use Binance market data hook
  const {
    getMarketDataByCategory,
    getTopGainers,
    getTopLosers,
    getHighestVolume,
    isConnected: isBinanceConnected,
  } = useBinanceMarketData();

  // Get market data for different categories
  const getMarketDataForCategory = (category: string): MarketData[] => {
    switch (category) {
      case 'Crypto':
        return getMarketDataByCategory('crypto');
      case 'Top Movers':
        return [...getTopGainers(10), ...getTopLosers(10)];
      case 'Popular':
        return getHighestVolume(10);
      case 'Forex':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include forex pairs
        return [];
      case 'Commodities':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include commodities
        return [];
      case 'Stocks':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include stocks
        return [];
      default:
        return getMarketDataByCategory('crypto');
    }
  };

  const categories = [
    { id: 'Crypto', name: 'Cryptocurrencies', icon: '🪙' },
    { id: 'Top Movers', name: 'Top Movers', icon: '📈' },
    { id: 'Popular', name: 'Popular', icon: '⭐' },
    { id: 'Forex', name: 'Forex', icon: '💱' },
    { id: 'Commodities', name: 'Commodities', icon: '🛢️' },
    { id: 'Stocks', name: 'Stocks', icon: '📊' },
  ];

  const currentMarketData = getMarketDataForCategory(selectedCategory);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-100">Market Overview</h3>
        <div className="flex items-center space-x-1 text-xs text-gray-400">
          {isBinanceConnected ? (
            <>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Live</span>
            </>
          ) : (
            <>
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span>Offline</span>
            </>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex space-x-1 mb-4 overflow-x-auto scrollbar-hide">
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center space-x-2 px-3 py-2 text-xs font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${
              selectedCategory === category.id
                ? 'bg-[#2AB7A6] text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <span>{category.icon}</span>
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Market Data */}
      <div className="space-y-2">
        {currentMarketData.length > 0 ? (
          currentMarketData.slice(0, 10).map((item, index) => (
            <div
              key={item.symbol}
              onClick={() => onSymbolSelect?.(item.symbol)}
              className="flex items-center justify-between p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <div className="text-sm font-medium text-gray-300">
                  {index + 1}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-100">
                    {item.symbol}
                  </div>
                  <div className="text-xs text-gray-400">{item.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-100">
                  ${item.price}
                </div>
                                         <div
                           className={`text-xs font-medium flex items-center ${
                             item.isPositive ? 'text-green-400' : 'text-red-400'
                           }`}
                         >
                           {item.isPositive ? (
                             <TrendingUp className="h-3 w-3 mr-1" />
                           ) : (
                             <TrendingDown className="h-3 w-3 mr-1" />
                           )}
                           {item.change && item.change !== 'NaN%' ? item.change : '+0.00%'}
                         </div>
                <div className="text-xs text-gray-400">
                  Vol: {item.volume}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400">No market data available</p>
            {!isBinanceConnected && (
              <p className="text-xs text-gray-500 mt-2">Check your connection to Binance</p>
            )}
          </div>
        )}
      </div>

      {/* View All Button */}
      {currentMarketData.length > 10 && (
        <div className="mt-4 text-center">
          <button className="text-[#2AB7A6] hover:text-[#2AB7A6]/80 text-sm font-medium transition-colors">
            View All Markets
            <ChevronRight className="inline-block w-4 h-4 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketOverview;
